/**
 * Small beached rowboat (`hush.rowboat.v1`) — a ground piece for the hideout shore.
 *
 * Frame 140×60, support at the bottom-center (70, 60). A clinker-planked wooden hull sits on a low sand bed, rocked
 * slightly onto its keel (`tilt` 0..1, bow up to the right), with a thwart seat, two oarlocks on the gunwale and one
 * oar leaning against the hull. `wear` (0..1) fades the paint stripe, greys the planks and adds sea-salt streaks.
 * Anti-face: no dots or round holes — oarlocks are U-shapes and fixings are short strokes. No definitions, no
 * collision or gameplay geometry. Deterministic for the recipe; the seed varies the plank grain and sand.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface RowboatParams {
  readonly tilt: number; // 0..1, how far the hull is rocked bow-up
  readonly wear: number; // 0..1, weathering of paint and planks
}
export type RowboatRecipe = PieceRecipe<'hush.rowboat.v1', RowboatParams>;

export const ROWBOAT_FRAME = { w: 140, h: 60 } as const;

export const HUSH_ROWBOAT: RowboatRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.rowboat.beached.v1', generatorId: 'hush.rowboat.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { tilt: 0.5, wear: 0.4 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi)) throw new Error(`hush.rowboat.v1: ${k} must be ${lo}..${hi}`);
  return v;
}

const W = ROWBOAT_FRAME.w, H = ROWBOAT_FRAME.h;
const clamp = (p: Point): Point => ({ x: Math.min(W - 2, Math.max(2, p.x)), y: Math.min(H - 1, Math.max(2, p.y)) });

export function buildRowboat(recipe: RowboatRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.rowboat.v1');
  checkScope(scope);
  const tilt = num(recipe.params, 'tilt', 0, 1);
  const wear = num(recipe.params, 'wear', 0, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // Rock the hull about its keel contact (bow up, right); small so the boat still reads as resting.
  const pivot = { x: 66, y: 54 };
  const ang = -(1.5 + tilt * 5.5) * Math.PI / 180;
  const ca = Math.cos(ang), sa = Math.sin(ang);
  const rot = (p: Point): Point => clamp({
    x: pivot.x + (p.x - pivot.x) * ca - (p.y - pivot.y) * sa,
    y: pivot.y + (p.x - pivot.x) * sa + (p.y - pivot.y) * ca,
  });

  // Hull profile: stern (left) is a squat transom, bow (right) rises to a point.
  const sternX = 14, bowX = 126;
  const gunwale = (t: number): Point => ({ x: sternX + (bowX - sternX) * t, y: 27 - 2 * t - 9 * Math.pow(t, 4) });
  const keel = (t: number): Point => {
    const x = sternX + 4 + (bowX - 14 - sternX - 4) * t;
    return { x, y: 53 - 13 * Math.pow(Math.max(0, t - 0.55) / 0.45, 1.8) - 5 * Math.pow(1 - t, 3) };
  };
  const K = 16;
  const top: Point[] = [], bottom: Point[] = [];
  for (let i = 0; i <= K; i++) { top.push(rot(gunwale(i / K))); bottom.unshift(rot(keel(i / K))); }
  const hullPts = [...top, rot({ x: bowX + 1, y: 17 }), ...bottom];

  // ---- sand bed: a low mound under the keel so the support sits on paint ----
  const sandBase = mix(roles.paperBase, roles.paperShadow, 0.35);
  let sand = el('path', {
    d: `M${n(6)} ${n(H)}Q${n(26)} ${n(H - 6.5)} ${n(70)} ${n(H - 7)}Q${n(112)} ${n(H - 6.5)} ${n(134)} ${n(H)}Z`,
    fill: sandBase, stroke: roles.paperShadow, 'stroke-width': 0.8,
  });
  sand += el('path', {
    d: `M${n(30)} ${n(H - 3.5)}Q${n(70)} ${n(H - 5.5)} ${n(110)} ${n(H - 3.5)}`,
    fill: 'none', stroke: roles.paperShadow, 'stroke-width': 3, opacity: 0.5, 'stroke-linecap': 'round',
  });
  for (let i = 0; i < 6; i++) {
    const x = 18 + i * 19 + (rnd(`sand.${i}.x`) - 0.5) * 8;
    const y = H - 1.6 - rnd(`sand.${i}.y`) * 2.4;
    sand += el('path', { d: `M${n(x - 2.5)} ${n(y)}L${n(x + 2.5)} ${n(y - 0.4)}`, stroke: roles.paperLight, 'stroke-width': 0.8, 'stroke-linecap': 'round' });
  }

  // ---- oar leaning behind the hull (handle on the sand, blade up on the stern side) ----
  const oarFoot = { x: 34, y: H - 3 }, oarTop = { x: 10 + rnd('oar.lean') * 4, y: 6 };
  const odx = oarTop.x - oarFoot.x, ody = oarTop.y - oarFoot.y, ol = Math.hypot(odx, ody);
  const onx = -ody / ol, ony = odx / ol;
  const along = (t: number, off: number): Point => clamp({ x: oarFoot.x + odx * t + onx * off, y: oarFoot.y + ody * t + ony * off });
  const shaft = poly([along(0, -1.4), along(0.62, -1.3), along(0.62, 1.3), along(0, 1.4)]);
  const blade = poly([along(0.6, -1.4), along(0.7, -3.8), along(0.98, -3.6), along(1, 0), along(0.98, 3.6), along(0.7, 3.8), along(0.6, 1.4)]);
  const oarWood = mix(roles.woodLight, roles.salt, wear * 0.35);
  let oar = el('path', { d: shaft, fill: oarWood, stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round' });
  oar += el('path', { d: blade, fill: oarWood, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
  oar += el('path', { d: poly([along(0.72, 0), along(0.95, 0)], false), stroke: roles.woodShadow, 'stroke-width': 0.8, opacity: 0.7 });

  // ---- hull: base shape, then clinker plank lines between gunwale and keel ----
  const plankBase = mix(roles.woodBase, roles.stoneLight, wear * 0.4);
  let hull = el('path', { d: poly(hullPts), fill: plankBase, stroke: outline, 'stroke-width': 1.4, 'stroke-linejoin': 'round' });
  const strakes = 4;
  for (let s = 1; s <= strakes; s++) {
    const f = s / (strakes + 1);
    const line: Point[] = [];
    for (let i = 0; i <= K; i++) {
      const g = gunwale(i / K), k = keel(i / K);
      const tEnd = i === K ? 0.15 : 1; // strakes converge toward the bow stem
      line.push(rot({ x: g.x + (k.x - g.x) * f * tEnd, y: g.y + (k.y - g.y) * f * tEnd }));
    }
    hull += el('path', { d: poly(line, false), fill: 'none', stroke: roles.woodShadow, 'stroke-width': 1.1, 'stroke-linecap': 'round' });
    hull += el('path', { d: poly(line.slice(1, K).map(p => ({ x: p.x, y: p.y + 1.2 })), false), fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.7, opacity: 0.55 - wear * 0.25 });
  }
  // Grain flecks: short strokes, never dots.
  for (let i = 0; i < 9; i++) {
    const t = 0.08 + 0.8 * rnd(`grain.${i}.t`), f = 0.15 + 0.7 * rnd(`grain.${i}.f`);
    const g = gunwale(t), k = keel(t);
    const p = rot({ x: g.x + (k.x - g.x) * f, y: g.y + (k.y - g.y) * f });
    hull += el('path', { d: `M${n(p.x - 3)} ${n(p.y)}L${n(p.x + 3)} ${n(p.y - 0.3)}`, stroke: roles.woodShadow, 'stroke-width': 0.6, opacity: 0.5 });
  }
  // Painted stripe under the gunwale, faded by wear; a darker belly band near the keel.
  const stripe: Point[] = [];
  for (let i = 0; i <= K - 2; i++) { const g = gunwale(i / K); stripe.push(rot({ x: g.x, y: g.y + 3.2 })); }
  hull += el('path', { d: poly(stripe, false), fill: 'none', stroke: mix(roles.clothBlue, roles.salt, wear * 0.6), 'stroke-width': 3, opacity: 0.9 - wear * 0.35 });
  const belly: Point[] = [];
  for (let i = 1; i < K - 3; i++) { const k = keel(i / K); belly.push(rot({ x: k.x, y: k.y - 2 })); }
  hull += el('path', { d: poly(belly, false), fill: 'none', stroke: roles.woodShadow, 'stroke-width': 3, opacity: 0.55 });
  // Salt streaks from wear.
  const nSalt = Math.round(wear * 6);
  for (let i = 0; i < nSalt; i++) {
    const t = 0.1 + 0.75 * rnd(`salt.${i}.t`);
    const a = rot({ x: gunwale(t).x, y: gunwale(t).y + 5 }), b = rot({ x: gunwale(t).x + 1.5, y: gunwale(t).y + 11 + rnd(`salt.${i}.l`) * 8 });
    hull += el('path', { d: `M${n(a.x)} ${n(a.y)}L${n(b.x)} ${n(b.y)}`, stroke: roles.salt, 'stroke-width': 1, opacity: 0.45, 'stroke-linecap': 'round' });
  }

  // ---- rim: gunwale rail, transom edge, thwart end, oarlocks ----
  let rim = el('path', { d: poly(top, false), fill: 'none', stroke: roles.woodShadow, 'stroke-width': 2.6, 'stroke-linecap': 'round' });
  rim += el('path', { d: poly(top.map(p => ({ x: p.x, y: p.y - 0.6 })), false), fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.9, opacity: 0.8 });
  const tr0 = rot({ x: sternX, y: 27 }), tr1 = rot(keel(0));
  rim += el('path', { d: `M${n(tr0.x)} ${n(tr0.y)}L${n(tr1.x)} ${n(tr1.y)}`, stroke: outline, 'stroke-width': 1.6, 'stroke-linecap': 'round' });
  // Thwart: a seat plank seen above the rail amidships.
  const th0 = rot({ x: gunwale(0.42).x - 9, y: gunwale(0.42).y - 2.4 }), th1 = rot({ x: gunwale(0.42).x + 9, y: gunwale(0.42).y - 2.6 });
  rim += el('path', { d: `M${n(th0.x)} ${n(th0.y)}L${n(th1.x)} ${n(th1.y)}`, stroke: roles.woodBase, 'stroke-width': 2.2, 'stroke-linecap': 'round' });
  rim += el('path', { d: `M${n(th0.x)} ${n(th0.y)}L${n(th1.x)} ${n(th1.y)}`, stroke: outline, 'stroke-width': 0.6, opacity: 0.6, 'stroke-dasharray': '18 40' });
  // Oarlocks: open U-horns on short posts, one pair at the thwart (fore and aft of it).
  let locks = '';
  for (const t of [0.3, 0.56]) {
    const g = gunwale(t);
    const p0 = rot({ x: g.x, y: g.y }), p1 = rot({ x: g.x, y: g.y - 3 });
    const l = rot({ x: g.x - 2.4, y: g.y - 6 }), r = rot({ x: g.x + 2.4, y: g.y - 6 });
    locks += el('path', { d: `M${n(p0.x)} ${n(p0.y)}L${n(p1.x)} ${n(p1.y)}`, stroke: roles.stoneShadow, 'stroke-width': 1.4, 'stroke-linecap': 'round' });
    locks += el('path', { d: `M${n(l.x)} ${n(l.y)}Q${n(p1.x)} ${n(p1.y + 1.2)} ${n(r.x)} ${n(r.y)}`, fill: 'none', stroke: roles.stoneShadow, 'stroke-width': 1.3, 'stroke-linecap': 'round' });
  }

  const body = partGroup(parts, 'rowboat-sand', sand)
    + partGroup(parts, 'rowboat-oar', oar)
    + partGroup(parts, 'rowboat-hull', hull)
    + partGroup(parts, 'rowboat-rim', rim + locks);
  const support = { x: W / 2, y: H };
  const bow = rot({ x: bowX + 1, y: 17 }), stern = rot({ x: sternX, y: 27 });
  return {
    frame: { ...ROWBOAT_FRAME },
    body,
    anchors: { support, bow: { x: bow.x, y: bow.y }, stern: { x: stern.x, y: stern.y }, oar_tip: { x: oarTop.x, y: oarTop.y } },
    parts,
  };
}
